import { Star, Quote } from "lucide-react";

type Bi = { en: string; bn: string };

interface Props {
  isBn: boolean;
  displayFont?: string;
  bodyFont?: string;
}

const pick = (b: Bi, isBn: boolean) => (isBn ? b.bn : b.en);

const HEADING: Bi = {
  en: "Loved by doctors across Bangladesh",
  bn: "বাংলাদেশের ডাক্তারদের প্রিয়",
};
const KICKER: Bi = { en: "Testimonials", bn: "প্রশংসাপত্র" };
const SUB: Bi = {
  en: "Real quotes from clinicians using Doctorsx every day in chambers, hospitals and telemedicine.",
  bn: "চেম্বার, হাসপাতাল ও টেলিমেডিসিনে প্রতিদিন Doctorsx ব্যবহারকারী চিকিৎসকদের সত্যিকারের মতামত।",
};

const FEATURED: {
  quote: Bi;
  name: string;
  role: Bi;
  rating: number;
  initials: string;
  color: string;
} = {
  quote: {
    en: "Doctorsx replaced three tools for me — prescriptions, WhatsApp follow-ups, and my daily income register. The Bengali Rx is beautiful and my patients notice.",
    bn: "Doctorsx আমার তিনটি টুল একসাথে করেছে — প্রেসক্রিপশন, WhatsApp ফলোআপ ও দৈনিক হিসাব খাতা। বাংলা Rx সত্যিই সুন্দর, রোগীরাও লক্ষ্য করেন।",
  },
  name: "Dr. Farhana Rahman",
  role: {
    en: "Consultant, Gynecology · Dhanmondi",
    bn: "কনসালট্যান্ট, গাইনি · ধানমন্ডি",
  },
  rating: 5,
  initials: "FR",
  color: "from-rose-500 to-orange-500",
};

const ITEMS: {
  quote: Bi;
  name: string;
  role: Bi;
  rating: number;
  initials: string;
  color: string;
}[] = [
  {
    quote: {
      en: "The AI ambient scribe saves me 90 minutes a day. I just talk to my patient — the SOAP note is ready in Bengali.",
      bn: "AI অ্যাম্বিয়েন্ট স্ক্রাইব আমার প্রতিদিন ৯০ মিনিট বাঁচায়। শুধু রোগীর সাথে কথা বলি — বাংলায় SOAP নোট রেডি।",
    },
    name: "Dr. Sabbir Ahmed",
    role: { en: "Medicine Specialist · Chattogram", bn: "মেডিসিন বিশেষজ্ঞ · চট্টগ্রাম" },
    rating: 5,
    initials: "SA",
    color: "from-sky-500 to-blue-600",
  },
  {
    quote: {
      en: "No-show rate dropped from 22% to 7% after we turned on the WhatsApp agent. It pays for itself every week.",
      bn: "WhatsApp এজেন্ট চালু করার পর নো-শো রেট ২২% থেকে ৭%-এ নেমেছে। প্রতি সপ্তাহে খরচ উঠে আসে।",
    },
    name: "Dr. Nusrat Jahan",
    role: { en: "Pediatrics · Sylhet", bn: "শিশুরোগ · সিলেট" },
    rating: 5,
    initials: "NJ",
    color: "from-emerald-500 to-teal-600",
  },
  {
    quote: {
      en: "Offline prescription capture is a lifesaver during load-shedding. Nothing is ever lost, everything syncs back.",
      bn: "লোডশেডিং-এর সময় অফলাইন প্রেসক্রিপশন সত্যিই জীবনরক্ষাকারী। কিছুই হারায় না, সব সিঙ্ক হয়ে যায়।",
    },
    name: "Dr. Imran Kabir",
    role: { en: "GP · Rajshahi", bn: "জেনারেল ফিজিশিয়ান · রাজশাহী" },
    rating: 5,
    initials: "IK",
    color: "from-violet-500 to-indigo-600",
  },
  {
    quote: {
      en: "Care-gap alerts brought back 40+ diabetic patients overdue for HbA1c in one month. My staff loves the one-click recall.",
      bn: "কেয়ার-গ্যাপ অ্যালার্ট এক মাসে ৪০+ HbA1c-এর জন্য অপেক্ষমাণ ডায়াবেটিক রোগী ফিরিয়ে এনেছে। এক ক্লিকে রিকল স্টাফরাও পছন্দ করে।",
    },
    name: "Dr. Anika Chowdhury",
    role: { en: "Endocrinology · Dhaka", bn: "এন্ডোক্রাইনোলজি · ঢাকা" },
    rating: 5,
    initials: "AC",
    color: "from-fuchsia-500 to-pink-600",
  },
  {
    quote: {
      en: "Specialty templates for prenatal visits are exactly what OB/GYN needed. One click and the full workup is on paper.",
      bn: "প্রেনাটাল ভিজিটের জন্য স্পেশালিটি টেমপ্লেট ঠিক যা OB/GYN-এর দরকার ছিল। এক ক্লিকেই পুরো ওয়ার্কআপ।",
    },
    name: "Dr. Tahmina Islam",
    role: { en: "OB/GYN · Khulna", bn: "OB/GYN · খুলনা" },
    rating: 4,
    initials: "TI",
    color: "from-amber-500 to-orange-600",
  },
  {
    quote: {
      en: "The desktop app with tray notifications makes my chamber feel like a real clinic system. Very professional.",
      bn: "ট্রে নোটিফিকেশনসহ ডেস্কটপ অ্যাপ আমার চেম্বারকে সত্যিকারের ক্লিনিক সিস্টেমের মতো অনুভব করায়। অত্যন্ত পেশাদার।",
    },
    name: "Dr. Rakib Hasan",
    role: { en: "Cardiology · Dhaka", bn: "কার্ডিওলজি · ঢাকা" },
    rating: 5,
    initials: "RH",
    color: "from-cyan-500 to-sky-600",
  },
];

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i < rating ? "fill-amber-400 text-amber-400" : "text-gray-300"}`}
        />
      ))}
    </div>
  );
}

export default function Testimonials({ isBn, displayFont, bodyFont }: Props) {
  const dFont = displayFont ?? "'Fraunces', ui-serif, Georgia, serif";
  const bFont = bodyFont ?? "'Inter', ui-sans-serif, system-ui";

  return (
    <section
      id="testimonials"
      className="max-w-7xl mx-auto mt-24 md:mt-40 px-4"
      style={{ fontFamily: bFont }}
    >
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto mb-14">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold tracking-wide uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
          {pick(KICKER, isBn)}
        </div>
        <h2
          className="mt-4 text-4xl md:text-5xl font-semibold tracking-tight text-gray-900"
          style={{ fontFamily: dFont }}
        >
          {pick(HEADING, isBn)}
        </h2>
        <p className="mt-4 text-gray-600 text-base md:text-lg leading-relaxed">
          {pick(SUB, isBn)}
        </p>

        {/* Rating summary */}
        <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm">
          <Stars rating={5} />
          <span className="text-sm font-semibold text-gray-900">4.9 / 5</span>
          <span className="text-sm text-gray-500">
            {isBn ? "৮০০+ চিকিৎসকের রিভিউ" : "800+ verified doctors"}
          </span>
        </div>
      </div>

      {/* Featured quote */}
      <div className="relative rounded-3xl border border-gray-200 bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-8 md:p-12 overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-blue-200/40 blur-3xl"
        />
        <div
          aria-hidden
          className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-cyan-200/40 blur-3xl"
        />
        <div className="relative z-10 grid md:grid-cols-[auto,1fr] gap-6 md:gap-10 items-start">
          <div
            className={`w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br ${FEATURED.color} text-white flex items-center justify-center text-2xl md:text-3xl font-bold shadow-lg shrink-0`}
          >
            {FEATURED.initials}
          </div>
          <div>
            <Quote className="w-10 h-10 text-blue-400/70 mb-3" />
            <blockquote
              className="text-xl md:text-2xl leading-relaxed text-gray-800 font-medium"
              style={{ fontFamily: dFont }}
            >
              “{pick(FEATURED.quote, isBn)}”
            </blockquote>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <div>
                <div className="font-semibold text-gray-900">{FEATURED.name}</div>
                <div className="text-sm text-gray-600">{pick(FEATURED.role, isBn)}</div>
              </div>
              <div className="h-6 w-px bg-gray-300 hidden sm:block" />
              <Stars rating={FEATURED.rating} />
            </div>
          </div>
        </div>
      </div>

      {/* Grid of quotes */}
      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {ITEMS.map((item, i) => (
          <figure
            key={i}
            className="group relative rounded-2xl border border-gray-200 bg-white p-6 hover:border-blue-300 hover:shadow-lg transition-all duration-300 flex flex-col"
          >
            <Stars rating={item.rating} />
            <blockquote className="mt-4 text-gray-700 leading-relaxed text-[15px] flex-1">
              “{pick(item.quote, isBn)}”
            </blockquote>
            <figcaption className="mt-6 flex items-center gap-3 pt-4 border-t border-gray-100">
              <div
                className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} text-white flex items-center justify-center text-sm font-bold shrink-0`}
              >
                {item.initials}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-gray-900 text-sm truncate">
                  {item.name}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {pick(item.role, isBn)}
                </div>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}
